import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import checkoutTokenHandoffLogger from "@/components/CheckoutTokenHandoff/checkoutTokenHandoffLogger";
import { ROUTES } from "@/constants";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useAuth from "@/services/useAuth";
import decodeJwtClaims from "@/utils/decodeJwtClaims";

const ACCESS_TOKEN_PARAM = "access_token";

// Module-scoped guard: StrictMode/HMR/Suspense remounts must not replay the
// handoff once it has resolved. Mirrors `hasSilentSsoBootRun` in
// SilentSsoHandoff — `vi.clearAllMocks` cannot reset module-scoped state, so
// tests re-import the module to reset it.
let hasCheckoutTokenBootRun = false;

type CheckoutTokenHandoffDeps = {
  isAuthenticated: boolean;
  isLoading: boolean;
  isCheckoutRoute: boolean;
  token: string | null;
  stripAccessToken: () => void;
  saveToken: (jwt: string) => void;
};

// Returns `true` once the handoff has been resolved (accepted or refused) so
// the caller can latch the boot guard. Returns `false` only while Kinde is
// still resolving, so a later re-render can retry.
function attemptCheckoutTokenHandoff(deps: CheckoutTokenHandoffDeps): boolean {
  const { isAuthenticated, isLoading, isCheckoutRoute, token, stripAccessToken, saveToken } = deps;

  // Evaluate only once Kinde has resolved. `isAuthenticated` is always false
  // while the SDK is loading, which is exactly what lets a fresh-tab handoff
  // slip past a guard that checks it too early.
  if (isLoading) {
    return false;
  }

  if (!token) {
    return true;
  }

  // Strip on every resolved branch below, including the refusals, so the token
  // never lingers in the address bar, browser history or outbound Referer.
  // Routed through react-router so `useLocation().search` stays in sync and
  // nothing can navigate the token back into the address bar.
  stripAccessToken();

  // The only legitimate producer (the SpicyChat Android checkout WebView)
  // targets /checkout. Honour the handoff nowhere else.
  if (!isCheckoutRoute) {
    return true;
  }

  // Refuse when any Kinde session is already established: a signed-in tab must
  // never be switched to a URL-supplied identity.
  if (isAuthenticated) {
    return true;
  }

  if (!decodeJwtClaims(token)) {
    checkoutTokenHandoffLogger.warn("access_token did not decode; ignoring handoff");
    return true;
  }

  saveToken(token);
  return true;
}

/**
 * Consumes the `?access_token=` checkout handoff produced by the SpicyChat
 * Android WebView (`chargebee.tsx`). The WebView is a fresh browsing context
 * with no Kinde session, so this seeds the client auth state that `/checkout`
 * needs before it renders. Mount once inside `<KindeProvider>`.
 *
 * Hardening vs. the previous root-level effect: scoped to /checkout, stripped
 * from the URL on every branch, refused when a Kinde session already exists,
 * and guarded so a malformed token cannot throw.
 */
const CheckoutTokenHandoff = (): null => {
  const { isAuthenticated, isLoading } = useKindeAuth();
  const { saveToken } = useAuth();
  const { pagePath } = useLocalizedLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tolerate a trailing slash: strict equality would treat `/checkout/` as a
  // non-checkout route and silently strip + discard the token with no retry.
  const normalizedPagePath = pagePath.length > 1 ? pagePath.replace(/\/+$/, "") : pagePath;
  const isCheckoutRoute = normalizedPagePath === ROUTES.checkout;

  const token = searchParams.get(ACCESS_TOKEN_PARAM);

  const stripAccessToken = useCallback(() => {
    try {
      const next = new URLSearchParams(searchParams);
      if (!next.has(ACCESS_TOKEN_PARAM)) return;
      next.delete(ACCESS_TOKEN_PARAM);
      setSearchParams(next, { replace: true });
    } catch (error) {
      checkoutTokenHandoffLogger.warn("strip failed; param may persist in URL", { error });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (hasCheckoutTokenBootRun) {
      return;
    }
    const isResolved = attemptCheckoutTokenHandoff({ isAuthenticated, isLoading, isCheckoutRoute, token, stripAccessToken, saveToken });
    if (isResolved) {
      hasCheckoutTokenBootRun = true;
    }
  }, [isAuthenticated, isLoading, isCheckoutRoute, token, stripAccessToken, saveToken]);

  return null;
};

export default CheckoutTokenHandoff;
